import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ReportData } from '@/api/types';
import { useFormat } from '@/hooks/useFormat';
import { theme } from '@/style/theme';

const CHART_COLORS = [
  theme.chartQual1,
  theme.chartQual2,
  theme.chartQual3,
  theme.chartQual4,
  theme.chartQual5,
  theme.chartQual6,
];

function axisProps() {
  return {
    stroke: theme.tableTextLight,
    tick: { fill: theme.tableTextLight, fontSize: 11 },
    tickLine: false,
    axisLine: false,
  } as const;
}

function tooltipProps() {
  return {
    contentStyle: {
      background: theme.tooltipBackground,
      border: `1px solid ${theme.tooltipBorder}`,
      borderRadius: 4,
      fontSize: 12,
      color: theme.tooltipText,
    },
    labelStyle: { color: theme.tooltipText },
  } as const;
}

/**
 * Text legend: the series are distinguished by name and dash pattern first,
 * colour only second, so a chart stays readable without relying on hue.
 */
function legendProps() {
  return {
    verticalAlign: 'top' as const,
    align: 'right' as const,
    height: 18,
    iconType: 'plainline' as const,
    iconSize: 10,
    wrapperStyle: { fontSize: 11, color: theme.tableTextLight },
  } as const;
}

export function NetWorthArea({ data }: { data: ReportData }) {
  const format = useFormat();
  const series = data.series ?? [];
  return (
    <ResponsiveContainer width="100%" height={140}>
      <AreaChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id="netWorthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.chartQual1} stopOpacity={0.16} />
            <stop offset="100%" stopColor={theme.chartQual1} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <XAxis dataKey="label" hide />
        <YAxis {...axisProps()} width={78} tickFormatter={value => format(value)} />
        <Tooltip {...tooltipProps()} formatter={(value: number) => format(value)} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={theme.chartQual1}
          strokeWidth={2}
          fill="url(#netWorthFill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CumulativeArea({ data }: { data: ReportData }) {
  const format = useFormat();
  const series = data.series ?? [];
  const hasSecondary = series.some(point => point.secondary !== null);
  return (
    <ResponsiveContainer width="100%" height={130}>
      <AreaChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.chartQual1} stopOpacity={0.14} />
            <stop offset="100%" stopColor={theme.chartQual1} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={theme.tableBorder} vertical={false} />
        <XAxis dataKey="label" {...axisProps()} minTickGap={28} />
        <YAxis {...axisProps()} width={72} tickFormatter={value => format(value)} />
        <Tooltip {...tooltipProps()} formatter={(value: number) => format(value)} />
        {hasSecondary && <Legend {...legendProps()} />}
        {hasSecondary && (
          <Area
            type="monotone"
            dataKey="secondary"
            name={data.lineLabel ?? '上月'}
            stroke={theme.tableTextLight}
            strokeDasharray="5 4"
            fill="none"
            strokeWidth={1.5}
          />
        )}
        <Area
          type="monotone"
          dataKey="value"
          name={data.areaLabel ?? 'Value'}
          stroke={theme.chartQual1}
          strokeWidth={2}
          fill="url(#spendFill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CashFlowBars({ data }: { data: ReportData }) {
  const format = useFormat();
  const bars = data.bars ?? [];
  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart
        data={bars}
        margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
        barSize={22}
      >
        <XAxis dataKey="label" {...axisProps()} />
        <YAxis {...axisProps()} width={56} tickFormatter={v => format(v)} />
        <Tooltip {...tooltipProps()} formatter={(value: number) => format(value)} />
        <Bar dataKey="value" radius={[2, 2, 0, 0]}>
          {bars.map(bar => (
            <Cell
              key={bar.label}
              fill={
                bar.color === 'positive'
                  ? theme.numberPositive
                  : theme.numberNegative
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CategoryDonut({ data }: { data: ReportData }) {
  const format = useFormat();
  const slices = data.donut ?? [];
  return (
    <div className="flex items-center gap-3">
      <ResponsiveContainer width="50%" height={140}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            innerRadius={34}
            outerRadius={58}
            paddingAngle={1}
          >
            {slices.map((slice, index) => (
              <Cell
                key={slice.name}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                stroke="none"
              />
            ))}
          </Pie>
          <Tooltip {...tooltipProps()} formatter={(value: number) => format(value)} />
        </PieChart>
      </ResponsiveContainer>
      <ul className="flex-1 space-y-1 text-xs text-table-text-light">
        {slices.map((slice, index) => (
          <li key={slice.name} className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: CHART_COLORS[index % CHART_COLORS.length] }}
            />
            <span className="min-w-0 flex-1 truncate">{slice.name}</span>
            <span className="tnum">{format(slice.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
